import { useEffect, useState, useCallback, useRef } from "react";
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Printer, FilePlus2, ListPlus, BadgeDollarSign } from "lucide-react";
import { Link } from "react-router-dom";
import { PrintOverlay, SPKPrintView } from "@/components/printDocs";
import { PrintModeToggle, SpkHargaDialog } from "@/components/SpkHarga";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { PageHeader, DataTable, Loading } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtTon, fmtRp } from "@/lib/utils";

const kgf = (n) => Number(n || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });
const PRICE_COLS = [
  { key: "unitHarga", label: "Unit Harga", render: (r) => <span className={`rounded border px-1.5 py-0.5 text-[10px] ${r.unitHarga === "METER" ? "border-violet-500/40 text-violet-300" : "border-sky-500/40 text-sky-300"}`}>{r.unitHarga || "TON"}</span> },
  { key: "hargaSatuan", label: "Harga Satuan", render: (r) => <span className="font-mono text-slate-400">{fmtRp(r.hargaSatuan || 0)}<span className="text-[10px] text-slate-600">/{r.unitHarga === "METER" ? "m" : "ton"}</span></span> },
  { key: "totalHarga", label: "Total Harga", render: (r) => <span className="font-mono text-slate-100" title={`qty selesai ${r.realisasi ?? 0}`}>{fmtRp(r.totalHarga || 0)}</span> },
];

export default function DeliveryList() {
  const [projects, setProjects] = useState([]);
  const [spks, setSpks] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [spkId, setSpkId] = useState("");
  const [marks, setMarks] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const fileRef = useRef(null);
  const { user } = useApp();
  const [mode, setMode] = useState("new");
  const [newBusy, setNewBusy] = useState(false);
  const [newResult, setNewResult] = useState(null);
  const [newMarks, setNewMarks] = useState([]);
  const [printOpen, setPrintOpen] = useState(false);
  const [commercial, setCommercial] = useState(false);
  const [hargaOpen, setHargaOpen] = useState(false);
  const newFileRef = useRef(null);

  const downloadSpkTemplate = async () => {
    try {
      const res = await api.get("/template/spk", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = "template_spk.xlsx"; a.click();
      toast.success("Template SPK terunduh / SPK模板已下载");
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const uploadSpk = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNewBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/spk/upload", fd);
      setNewResult(data);
      const m = await api.get(`/markings?spkId=${data.spk.id}&limit=500`);
      setNewMarks(m.data);
      toast.success(`SPK ${data.spk.no} dibuat · ${data.created} marking`);
    } catch (e2) { toast.error(fmtErr(e2)); }
    setNewBusy(false);
    if (newFileRef.current) newFileRef.current.value = "";
  };

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!projectId) return;
    api.get(`/spk?projectId=${projectId}`).then((r) => { setSpks(r.data); setSpkId(""); }).catch(() => {});
  }, [projectId]);

  const loadMarks = useCallback(() => {
    if (!spkId) { setMarks(null); return; }
    api.get(`/markings?spkId=${spkId}&limit=500`).then((r) => setMarks(r.data)).catch(() => {});
  }, [spkId]);
  useEffect(() => { loadMarks(); }, [loadMarks]);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/template/delivery-list", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = String(res.data.type).includes("csv") ? "template_delivery_list.csv" : "template_delivery_list.xlsx";
      a.click();
      toast.success("Template terunduh / 模板已下载");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!spkId) { toast.error("Pilih SPK terlebih dahulu"); e.target.value = ""; return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/spk/${spkId}/delivery-list`, fd);
      setLastResult(data);
      toast.success(`Selesai: ${data.created} marking dibuat, ${data.updated} diperbarui, ${data.skipped} dilewati`);
      loadMarks();
    } catch (e2) { toast.error(fmtErr(e2)); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const spk = spks.find((s) => s.id === spkId);

  return (
    <div className="space-y-5">
      <PageHeader id="Upload SPK" zh="派工单上传" desc="Buat SPK lengkap dari template Excel (info SPK + daftar marking) sehingga hasil cetak sesuai format SPK resmi, atau tambahkan marking ke SPK yang sudah ada." />

      <div className="inline-flex overflow-hidden rounded border border-slate-800" data-testid="upload-mode-tabs">
        {[["new", FilePlus2, "Upload SPK Baru", "新建派工单"], ["append", ListPlus, "Tambah Marking ke SPK", "追加构件"]].map(([k, Icon, t, zh]) => (
          <button key={k} type="button" onClick={() => setMode(k)} className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs transition-colors ${mode === k ? "bg-sky-500/15 text-sky-300" : "text-slate-400 hover:text-slate-200"}`} data-testid={`upload-mode-${k}`}>
            <Icon className="h-3.5 w-3.5" />{t} <span className="cn text-[10px] opacity-70">{zh}</span>
          </button>
        ))}
      </div>

      {mode === "new" && (
        <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]" data-testid="upload-spk-new">
          <div className="panel space-y-4 p-5">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-200"><FileSpreadsheet className="h-4 w-4 text-emerald-400" /> Langkah 1 — Unduh Template SPK / 下载模板</div>
            <div className="rounded border border-slate-800 bg-[#0B1322] p-3 text-[11px] leading-relaxed text-slate-400">
              Template berisi 2 sheet:<br />
              <b className="text-slate-200">Sheet "SPK"</b> — isi kolom <i>Nilai</i>: No SPK (kosong = otomatis), <b className="font-mono text-slate-300">Kode Proyek*</b>, <b className="font-mono text-slate-300">Vendor*</b> (nama/singkatan), Area, Jenis Pekerjaan, Tanggal Mulai, Target Selesai, Harga per Ton, Menggantikan SPK, Status.<br />
              <b className="text-slate-200">Sheet "Marking"</b> — <span className="font-mono text-slate-300">marking, profile, panjang (mm), qty, unit berat, total berat, unit harga (TON/METER), harga satuan, keterangan</span>.<br />
              <span className="text-slate-500">Total harga = TON: unit berat × qty selesai ÷ 1000 × harga/ton · METER: qty selesai × panjang ÷ 1000 × harga/m. Kosong = TON dengan Harga per Ton SPK.</span>
            </div>
            <Button onClick={downloadSpkTemplate} variant="outline" className="w-full border-slate-700" data-testid="spk-template-download-button"><Download className="mr-2 h-4 w-4" />Download Template SPK (.xlsx)</Button>
            <div className="border-t border-slate-800 pt-4">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-200"><Upload className="h-4 w-4 text-sky-400" /> Langkah 2 — Upload Template Terisi / 上传</div>
              <input ref={newFileRef} type="file" accept=".xlsx,.xls" onChange={uploadSpk} disabled={newBusy}
                className="mt-3 w-full cursor-pointer rounded border border-dashed border-slate-700 bg-slate-900/50 p-3 text-xs text-slate-400 file:mr-3 file:rounded file:border-0 file:bg-sky-600 file:px-3 file:py-1.5 file:text-xs file:text-white hover:border-sky-500/50 disabled:opacity-40"
                data-testid="spk-file-input" />
              <p className="mt-2 text-[11px] text-slate-500">SPK baru dibuat beserta semua marking; qty & tonase dihitung otomatis. Marking yang sudah ada di SPK lain dilewati. Setelah berhasil, langsung bisa dicetak.</p>
            </div>
            {newResult && (
              <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs" data-testid="spk-upload-result">
                <div className="flex items-center gap-2 text-emerald-300"><CheckCircle2 className="h-4 w-4" /> SPK <b className="font-mono">{newResult.spk.no}</b> dibuat · {newResult.created} marking · {newResult.spk.qty} pcs · {fmtTon(newResult.spk.totalTon)} Ton</div>
                <div className="mt-1 text-slate-400">{newResult.spk.projectKode} · {newResult.spk.vendorName}{newResult.spk.vendorSingkatan ? ` (${newResult.spk.vendorSingkatan})` : ""} · Area {newResult.spk.area} · {newResult.spk.status}</div>
                {newResult.skipped?.length > 0 && <div className="mt-1 text-amber-400">Dilewati (sudah ada SPK): {newResult.skipped.join(", ")}</div>}
                {newResult.errors?.length > 0 && <div className="mt-1 text-amber-400">{newResult.errors.join(" · ")}</div>}
                <div className="mt-2 flex gap-2">
                  <Button size="sm" onClick={() => setPrintOpen(true)} className="bg-sky-600 hover:bg-sky-500" data-testid="spk-upload-print-button"><Printer className="mr-1.5 h-3.5 w-3.5" />Cetak SPK</Button>
                  <Link to="/spk" className="inline-flex items-center text-xs text-sky-400 hover:underline" data-testid="spk-upload-goto">Buka menu SPK →</Link>
                </div>
              </div>
            )}
          </div>
          <div className="panel p-4" data-testid="spk-upload-preview">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-display text-sm font-bold text-slate-200">Marking SPK Baru <span className="cn text-[11px] font-normal text-slate-500">新派工单构件</span></h3>
              {newResult && <span className="font-mono text-xs text-slate-500">{newResult.spk.no}</span>}
            </div>
            {!newResult ? (
              <div className="py-16 text-center text-sm text-slate-500" data-testid="spk-upload-preview-empty">Upload template SPK terisi untuk membuat SPK baru<br /><span className="cn text-xs">上传已填写的模板以创建派工单</span></div>
            ) : (
              <DataTable testid="spk-upload-markings-table" rows={newMarks} columns={[
                { key: "no", label: "No", render: (r, i) => <span className="font-mono text-slate-500">{i + 1}</span> },
                { key: "code", label: "Marking", render: (r) => <span className="font-mono text-amber-400">{r.code}</span> },
                { key: "profile", label: "Profile", render: (r) => <span className="text-xs">{r.profile || "—"}</span> },
                { key: "panjang", label: "Panjang (mm)", render: (r) => <span className="font-mono">{r.panjang != null ? kgf(r.panjang) : "—"}</span> },
                { key: "qty", label: "Qty", render: (r) => <span className="font-mono">{r.qty}</span> },
                { key: "unitBerat", label: "Unit (kg)", render: (r) => <span className="font-mono text-slate-400">{kgf((r.unitTon || 0) * 1000)}</span> },
                { key: "totalBerat", label: "Total (kg)", render: (r) => <span className="font-mono text-slate-400">{kgf(r.qty * (r.unitTon || 0) * 1000)}</span> },
                ...PRICE_COLS,
                { key: "ket", label: "Keterangan", render: (r) => <span className="text-xs text-slate-400">{r.keterangan || "—"}</span> },
              ]} />
            )}
          </div>
        </div>
      )}

      {printOpen && newResult && (
        <PrintOverlay testid="spk-upload-print-overlay" onClose={() => setPrintOpen(false)} toolbar={<PrintModeToggle commercial={commercial} onChange={setCommercial} testid="spk-upload-print-mode" />}>
          <SPKPrintView spk={newResult.spk} markings={newMarks} user={user} commercial={commercial} />
        </PrintOverlay>
      )}

      {mode === "append" && <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]" data-testid="upload-spk-append">
        <div className="panel space-y-4 p-5" data-testid="delivery-upload-panel">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-200">
            <FileSpreadsheet className="h-4 w-4 text-emerald-400" /> Langkah 1 — Unduh Template / 下载模板
          </div>
          <p className="text-xs text-slate-500">
            Header template: <b className="font-mono text-slate-300">marking, profile, panjang, qty, unit berat, total berat</b>. Isi baris sesuai daftar marking delivery.
          </p>
          <Button onClick={downloadTemplate} variant="outline" className="w-full border-slate-700" data-testid="delivery-template-download-button">
            <Download className="mr-2 h-4 w-4" />Download Template
          </Button>

          <div className="border-t border-slate-800 pt-4">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-200">
              <Upload className="h-4 w-4 text-sky-400" /> Langkah 2 — Pilih SPK & Upload / 选择派工单并上传
            </div>
            <div className="mt-3 space-y-3">
              <Select value={projectId} onValueChange={setProjectId} data-testid="delivery-project-select">
                <SelectTrigger className="border-slate-800 bg-slate-900/70 text-sm"><SelectValue placeholder="Pilih Proyek / 项目" /></SelectTrigger>
                <SelectContent className="bg-[#0D1524] border-slate-800 max-h-56">
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.kode} · {p.nama}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={spkId} onValueChange={setSpkId} disabled={!projectId} data-testid="delivery-spk-select">
                <SelectTrigger className="border-slate-800 bg-slate-900/70 text-sm"><SelectValue placeholder={projectId ? "Pilih SPK / 派工单" : "Pilih proyek dulu"} /></SelectTrigger>
                <SelectContent className="bg-[#0D1524] border-slate-800 max-h-56">
                  {spks.map((s) => <SelectItem key={s.id} value={s.id}>{s.no} · {s.vendorName}</SelectItem>)}
                </SelectContent>
              </Select>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={upload} disabled={busy || !spkId}
                className="w-full cursor-pointer rounded border border-dashed border-slate-700 bg-slate-900/50 p-3 text-xs text-slate-400 file:mr-3 file:rounded file:border-0 file:bg-sky-600 file:px-3 file:py-1.5 file:text-xs file:text-white hover:border-sky-500/50 disabled:opacity-40"
                data-testid="delivery-file-input" />
              <p className="text-[11px] text-slate-500">Format .xlsx atau .csv, maks 5MB. Marking yang sudah ada akan diperbarui, baru akan dibuat.</p>
            </div>
          </div>

          {lastResult && (
            <div className="rounded border border-slate-800 bg-[#0B1322] p-3 text-xs" data-testid="delivery-upload-result">
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle2 className="h-4 w-4" /> {lastResult.created} dibuat · {lastResult.updated} diperbarui · {lastResult.skipped} dilewati
              </div>
              {spk && <div className="mt-1 text-slate-400">{spk.no} → Qty: <b className="font-mono text-slate-200">{lastResult.spk.qty}</b> pcs · Total: <b className="font-mono text-slate-200">{lastResult.spk.totalTon}</b> Ton</div>}
              {lastResult.errors?.length > 0 && (
                <div className="mt-2 border-t border-slate-800 pt-2">
                  <div className="flex items-center gap-1.5 text-amber-400"><AlertTriangle className="h-3.5 w-3.5" /> Dilewati:</div>
                  {lastResult.errors.map((e, i) => <div key={i} className="mt-0.5 text-slate-500">{e}</div>)}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="panel p-4" data-testid="delivery-preview-panel">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="font-display text-sm font-bold text-slate-200">
              Marking di SPK <span className="cn text-[11px] font-normal text-slate-500">派工单构件清单</span>
            </h3>
            {spk && <span className="font-mono text-xs text-slate-500">{spk.no} · {fmtTon(spk.totalTon)} Ton</span>}
          </div>
          {!spkId ? (
            <div className="py-16 text-center text-sm text-slate-500" data-testid="delivery-preview-empty">
              Pilih proyek & SPK untuk melihat daftar marking<br /><span className="cn text-xs">选择项目和派工单以查看构件清单</span>
            </div>
          ) : !marks ? <Loading /> : (<>
            {user?.role === "admin" && (
              <div className="mb-2 flex justify-end">
                <Button size="sm" variant="outline" className="h-8 border-amber-500/40 text-xs text-amber-300 hover:bg-amber-500/10" onClick={() => setHargaOpen(true)} data-testid="delivery-edit-harga-button">
                  <BadgeDollarSign className="mr-1.5 h-3.5 w-3.5" /> Edit Harga per Marking (TON / METER)
                </Button>
              </div>
            )}
            <DataTable testid="delivery-markings-table" rows={marks} columns={[
              { key: "no", label: "No", render: (r, i) => <span className="font-mono text-slate-500">{i + 1}</span> },
              { key: "code", label: "Marking", render: (r) => <span className="font-mono text-amber-400">{r.code}</span> },
              { key: "profile", label: "Profile", render: (r) => <span className="text-xs">{r.profile || "—"}</span> },
              { key: "panjang", label: "Panjang (mm)", render: (r) => <span className="font-mono">{r.panjang != null ? r.panjang.toLocaleString("id-ID") : "—"}</span> },
              { key: "qty", label: "Qty", render: (r) => <span className="font-mono">{r.qty}</span> },
              { key: "unitBerat", label: "Unit Berat (kg)", render: (r) => <span className="font-mono text-slate-400">{r.unitTon != null ? (r.unitTon * 1000).toLocaleString("id-ID", { maximumFractionDigits: 2 }) : "—"}</span> },
              { key: "totalBerat", label: "Total Berat (kg)", render: (r) => <span className="font-mono text-slate-400">{r.unitTon != null ? (r.qty * r.unitTon * 1000).toLocaleString("id-ID", { maximumFractionDigits: 2 }) : "—"}</span> },
              ...PRICE_COLS,
            ]} />
            {hargaOpen && spk && <SpkHargaDialog spk={spk} onClose={() => setHargaOpen(false)} onSaved={() => api.get(`/markings?spkId=${spkId}&limit=500`).then((r) => setMarks(r.data)).catch(() => {})} />}
          </>)}
        </div>
      </div>}
    </div>
  );
}